import React, { useState } from 'react';
import { Box, Icon, Typography } from '@saviynt/design-system';

/**
 * AuditLogViewerRecordingFrameVideo
 *
 * Renders the video selector tile: thumbnail and metadata snippet.
 */
function AuditLogViewerRecordingFrameVideo({
  src,
  title,
  dateTime,
  duration,
  separator = '•',
  onClick,
}: {
  src: string;
  title: string;
  dateTime: string;
  duration: string;
  separator?: string;
  onClick: () => void;
}): React.ReactElement {
  const [failedToLoad, setFailedToLoad] = useState<boolean>(false);
  const hasPreview = Boolean(src);

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className='Sod-AuditLogViewer-recordingPanel-content-frame-video-selector'>
      <Box className='Sod-AuditLogViewer-recordingPanel-content-frame-video-selector-thumbnail'>
        {!hasPreview || failedToLoad ? (
          <Box className='Sod-AuditLogViewer-recordingPanel-content-frame-video-selector-fallback'>
            <Icon kind='PlayFilled' size='large' />
          </Box>
        ) : (
          <video
            src={src}
            controls={false}
            muted
            playsInline
            preload='metadata'
            onError={() => setFailedToLoad(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
      </Box>
      <Box className='Sod-AuditLogViewer-recordingPanel-content-frame-video-selector-metadata'>
        <Box className='Sod-AuditLogViewer-recordingPanel-content-frame-video-selector-metadata-header'>
          <Typography kind='body1'>{title}</Typography>
        </Box>
        <Box className='Sod-AuditLogViewer-recordingPanel-content-frame-video-selector-metadata-description'>
          <Box>
            <Typography kind='body1'>{dateTime}</Typography>
          </Box>
          <Box>
            <Typography kind='body1'>{separator}</Typography>
          </Box>
          <Box>
            <Typography kind='body1'>{duration}</Typography>
          </Box>
        </Box>
      </Box>
    </div>
  );
}

AuditLogViewerRecordingFrameVideo.defaultProps = {
  separator: '•',
};

/**
 * AuditLogViewerRecordingFrame
 *
 * Presentational frame for the recording selector tile inside the
 * session recording panel. This renders a thumbnail placeholder and
 * basic metadata (title, date/time and duration).
 *
 * @returns React element containing the recording preview frame.
 */
function AuditLogViewerRecordingFrame({
  baseIndex,
  onSelect,
  sessions,
}: {
  baseIndex: number;
  onSelect: (index: number) => void;
  sessions: {
    id: string;
    src: string;
    title: string;
    dateTime: string;
    duration: string;
  }[];
}): React.ReactElement {
  const tiles: {
    s: {
      id: string;
      src: string;
      title: string;
      dateTime: string;
      duration: string;
    };
    idx: number;
  }[] = [];

  if (baseIndex < sessions.length) {
    tiles.push({ s: sessions[baseIndex], idx: baseIndex });
  }

  if (baseIndex + 1 < sessions.length) {
    tiles.push({ s: sessions[baseIndex + 1], idx: baseIndex + 1 });
  }

  return (
    <Box className='Sod-AuditLogViewer-recordingPanel-content-frame'>
      {tiles.map(({ s, idx }) => (
        <AuditLogViewerRecordingFrameVideo
          key={s.id}
          src={s.src}
          title={s.title}
          dateTime={s.dateTime}
          duration={s.duration}
          onClick={() => onSelect(idx)}
        />
      ))}
    </Box>
  );
}

export default AuditLogViewerRecordingFrame;
